import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { Alert, DatePicker, Form, Input, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import dayjs from 'dayjs';
import {
  AgendaCommercialeDto,
  AgendaCommercialeDtoEsitoEnum,
  AgendaCommercialeDtoModalitaContattoEnum,
  RptAgendaCommercialeDto,
  RptOpportunityDto,
  SaveOrUpdateAgendaCommercialeRequest,
} from '@api/client';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import LinksButton from 'shared/design-system/components/links-button';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import { filterOption } from 'shared/utils/common.utils';
import AuthService from 'core/auth/auth.service';
import { getClFilters } from 'pages/private/management-tools/_redux/actions';
import { filtriClSelector } from 'pages/private/management-tools/_redux/selectors';
import { getUserInfo } from 'shared/_components/delegate-management/_redux/actions';
import useGetAllActiveUsers from 'shared/_hooks/useGetAllActiveUsers';
import { StorageKeysEnum } from 'enums/storageKeys.enum';
import {
  getAgendaFilterOptions,
  loadOpportunitiesForModal,
  saveOrUpdateAgendaAction,
} from '../../_redux/actions';
import { agendaFilterOptionsSelector } from '../../_redux/selectors';
import { esitoLabel } from '../../_utils/esitoLabel';

interface Props {
  show: boolean;
  setShow: (show: boolean) => void;
  defaultDate?: Date;
  editData?: RptAgendaCommercialeDto;
  prefilledOpportunity?: RptOpportunityDto;
  prefilledClienteName?: string;
  prefilledStrutturaName?: string;
  onSaved?: () => void;
}

const REQUIRED = [{ required: true, message: 'Campo obbligatorio' }];

const AddAgendaActionModal: React.FC<Props> = ({ show, setShow, defaultDate, editData, prefilledOpportunity, prefilledClienteName, prefilledStrutturaName, onSaved }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filtriCl = useAppSelector(filtriClSelector);
  const agendaFilterOptions = useAppSelector(agendaFilterOptionsSelector);
  const accounts = agendaFilterOptions?.accounts || [];
  const allUsers = useGetAllActiveUsers();
  const isEdit = !!editData?.id;
  const isAdmin = AuthService.isSuperAdmin();

  const [userInfo, setUserInfo] = useState<any>(() => AuthService.getUserInfoData());

  // Business unit disponibili in base al ruolo
  // Admin: lista completa da /cl/search-filters-values -> businessUnit
  // Non-admin: match tra user-info.struttureBusiness e /cl/search-filters-values -> onlyChildBusinessUnit
  //   - se 1 match -> preselezione + readonly
  //   - se >1 match: si filtrano le struttureBusiness di tipo INDUSTRY (case-insensitive);
  //     se restano >1 match -> Select libera, se resta 1 -> preselezione + readonly,
  //     se restano 0 -> fallback ai match originali
  const availableBusinessUnits = useMemo(() => {
    if (isAdmin) return filtriCl?.businessUnit || [];

    const childUnits = filtriCl?.onlyChildBusinessUnit || [];
    const userStrutture: { id?: number; idStruttura?: { nome?: string } }[] =
      userInfo?.struttureBusiness || [];

    // Escludiamo le strutture di tipologia delivery (DELIVERY, DELIVERY UNIT, SUB DELIVERY UNIT)
    const EXCLUDED_TYPES = new Set(['delivery', 'delivery unit', 'sub delivery unit']);
    const filteredUserStrutture = userStrutture.filter(
      (s) => !EXCLUDED_TYPES.has(s.idStruttura?.nome?.toLowerCase() ?? ''),
    );

    const userIds = new Set(filteredUserStrutture.map((s) => s.id).filter((x): x is number => x != null));
    let matches = childUnits.filter((bu) => bu.id != null && userIds.has(bu.id));

    if (matches.length > 1) {
      const nonIndustryIds = new Set(
        filteredUserStrutture
          .filter((s) => s.idStruttura?.nome?.toLowerCase() !== 'industry')
          .map((s) => s.id)
          .filter((x): x is number => x != null),
      );
      const filtered = childUnits.filter((bu) => bu.id != null && nonIndustryIds.has(bu.id));
      if (filtered.length > 0) matches = filtered;
    }

    return matches;
  }, [isAdmin, filtriCl?.businessUnit, filtriCl?.onlyChildBusinessUnit, userInfo]);

  // Se l'utente non è admin e ha esattamente 1 BU, viene pre-selezionata
  const autoBusinessUnitId = useMemo(() => {
    if (!isAdmin && availableBusinessUnits.length === 1) return availableBusinessUnits[0].id;
    return undefined;
  }, [isAdmin, availableBusinessUnits]);

  const showBusinessUnitSelect = isAdmin || availableBusinessUnits.length >= 1;
  const isBusinessUnitReadonly = !isAdmin && availableBusinessUnits.length === 1;

  const [opportunities, setOpportunities] = useState<RptOpportunityDto[]>([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);

  useEffect(() => {
    if (show) {
      if (!accounts.length) dispatch(getAgendaFilterOptions());
      if (!filtriCl?.businessUnit?.length && !filtriCl?.onlyChildBusinessUnit?.length) {
        dispatch(getClFilters());
      }
      if (!isAdmin && !userInfo?.struttureBusiness) {
        dispatch(getUserInfo()).unwrap().then((res) => {
          if (res) {
            localStorage.setItem(StorageKeysEnum.USER_INFO, JSON.stringify(res));
            setUserInfo(res);
          }
        });
      }
    }
    if (!show) {
      setOpportunities([]);
    }
  }, [show]);

  const fetchOpportunities = useCallback(
    async (fkCliente?: string, fkStrutturaBusiness?: number) => {
      if (!fkCliente) {
        setOpportunities([]);
        return;
      }
      setLoadingOpportunities(true);
      try {
        const result = await dispatch(
          loadOpportunitiesForModal({
            rptOpportunitySearchDto: {
              fkCliente,
              fkStrutturaBusiness: fkStrutturaBusiness != null ? [fkStrutturaBusiness] : undefined,
              paging: { page: 0, size: 9999 },
            },
          })
        ).unwrap();
        setOpportunities(result?.content || []);
      } catch {
        setOpportunities([]);
      } finally {
        setLoadingOpportunities(false);
      }
    },
    [dispatch]
  );

  // Al mount del modale, se c'è già un cliente (edit o prefilledOpportunity), carica le opportunity
  useEffect(() => {
    if (!show) return;
    if (isEdit && editData?.clienteId) {
      fetchOpportunities(editData.clienteId, editData.strutturaBusinessId);
    } else if (!isEdit && prefilledOpportunity?.fkCliente) {
      fetchOpportunities(prefilledOpportunity.fkCliente, prefilledOpportunity.fkStrutturaBusiness);
    }
  }, [show, isEdit]);

  const initialValues: AgendaCommercialeDto = useMemo(() => (isEdit
    ? {
        id: editData!.id,
        fkCliente: editData!.clienteId,
        nominativoCliente: editData!.nominativoCliente,
        fkStrutturaBusiness: editData!.strutturaBusinessId ?? autoBusinessUnitId,
        fkUtente: editData!.fkUtente,
        actionDate: editData!.actionDate ?? new Date(),
        modalitaContatto: editData!.modalitaContatto,
        esito: editData!.esito,
        fkOpportunity: editData!.opportunityId,
        amount: editData!.amount,
        margin: editData!.margin,
        nota: editData!.nota,
        notaEsito: editData!.notaEsito,
      }
    : {
        fkCliente: prefilledOpportunity?.fkCliente ?? undefined,
        nominativoCliente: undefined,
        fkStrutturaBusiness: prefilledOpportunity?.fkStrutturaBusiness ?? autoBusinessUnitId,
        fkUtente: undefined,
        actionDate: defaultDate
          ? dayjs(defaultDate).hour(12).minute(0).second(0).millisecond(0).toDate()
          : new Date(),
        modalitaContatto: undefined,
        esito: undefined,
        fkOpportunity: prefilledOpportunity?.pkOpportunity ?? undefined,
        amount: undefined,
        margin: undefined,
        nota: undefined,
        notaEsito: undefined,
      }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isEdit, editData, prefilledOpportunity, autoBusinessUnitId, defaultDate]);

  const handleSubmit = (values: AgendaCommercialeDto) => {
    const payload: SaveOrUpdateAgendaCommercialeRequest = {
      agendaCommercialeDto: {
        id: isEdit ? values.id : undefined,
        fkCliente: values.fkCliente,
        nominativoCliente: values.nominativoCliente,
        fkStrutturaBusiness: values.fkStrutturaBusiness ?? autoBusinessUnitId,
        fkUtente: values.fkUtente,
        actionDate: values.actionDate,
        modalitaContatto: values.modalitaContatto,
        esito: values.esito,
        fkOpportunity: prefilledOpportunity
          ? prefilledOpportunity.pkOpportunity
          : values.esito === AgendaCommercialeDtoEsitoEnum.Positivo ? values.fkOpportunity : undefined,
        amount: values.amount,
        margin: values.margin,
        nota: values.nota,
        notaEsito: values.esito ? values.notaEsito : undefined,
      },
    };

    dispatch(saveOrUpdateAgendaAction(payload))
      .unwrap()
      .then((res) => {
        if (res) {
          setShow(false);
          AppToastService.success(isEdit ? t('Attività modificata con successo') : t('Attività creata con successo'));
          onSaved?.();
        }
      });
  };

  const clientLabel =
    isEdit
      ? accounts.find((a) => a.idAccount === editData?.clienteId)?.nome ?? editData?.clienteId ?? '-'
      : prefilledOpportunity
      ? prefilledClienteName ?? accounts.find((a) => a.idAccount === prefilledOpportunity.fkCliente)?.nome ?? prefilledOpportunity.fkCliente ?? '-'
      : undefined;

  return (
    <Modal show={show} onHide={() => setShow(false)} size="xl" backdrop="static" keyboard={false}>
      <Modal.Header closeButton={false}>
        <Modal.Title className="title-modal-s" style={{ backgroundColor: 'f3f4f7', fontSize: '1em' }}>
          {isEdit ? t('Modifica attività') : t('Nuova attività')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize>
          {({ handleSubmit: _handleSubmit, handleChange, setFieldValue, values }) => {
            const handleChangeSelect = (field: string) => (value: any) => {
              setFieldValue(field, value);
            };

            const onChangeDate = (field: string) => (date: dayjs.Dayjs | null) => {
              // Usa mezzogiorno locale per evitare lo shift di fuso orario durante la serializzazione toISOString()
              setFieldValue(field, date ? date.hour(12).minute(0).second(0).millisecond(0).toDate() : null);
            };

            const handleEsitoChange = (value: string | undefined) => {
              setFieldValue('esito', value);
              if (!value) {
                setFieldValue('notaEsito', undefined);
              }
              if (value === AgendaCommercialeDtoEsitoEnum.Positivo && prefilledOpportunity?.pkOpportunity) {
                setFieldValue('fkOpportunity', prefilledOpportunity.pkOpportunity);
              }
            };

            const handleClienteChange = (value: string | undefined) => {
              setFieldValue('fkCliente', value);
              setFieldValue('fkOpportunity', undefined);
              // Ricarica sempre le opportunity per il nuovo cliente
              fetchOpportunities(value, undefined);
            };

            const isWon = values.esito === AgendaCommercialeDtoEsitoEnum.Positivo;

            return (
              <Form onFinish={_handleSubmit}>
                {/* Row 1: Cliente, Nominativo cliente, Data azione, Modalità contatto */}
                <Row>
                  <Col lg={3} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Cliente *')}</label>
                      {(isEdit && !isAdmin) || prefilledOpportunity ? (
                        <Input
                          className="w-100"
                          value={clientLabel}
                          disabled
                          style={{ backgroundColor: '#f5f5f5', color: '#555' }}
                        />
                      ) : (
                        <Form.Item name="fkCliente" className="mb-3" initialValue={values.fkCliente} rules={REQUIRED}>
                          <Select
                            showSearch
                            className="w-100"
                            optionFilterProp="children"
                            filterOption={filterOption}
                            allowClear
                            placeholder={t('Seleziona cliente')}
                            onChange={handleClienteChange}
                            value={values.fkCliente}
                            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                          >
                            {accounts.map((el) => (
                              <Select.Option key={el.idAccount} value={el.idAccount}>
                                {el.nome}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                    </div>
                  </Col>
                  <Col lg={3} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Nominativo cliente *')}</label>
                      <Form.Item
                        name="nominativoCliente"
                        className="mb-3"
                        initialValue={values.nominativoCliente}
                        rules={REQUIRED}
                      >
                        <Input
                          name="nominativoCliente"
                          placeholder={t('Inserisci nominativo cliente')}
                          value={values.nominativoCliente}
                          onChange={handleChange}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col lg={3} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Data azione *')}</label>
                      <Form.Item
                        name="actionDate"
                        className="mb-3"
                        initialValue={dayjs(initialValues.actionDate)}
                        rules={REQUIRED}
                      >
                        <DatePicker
                          className="w-100"
                          format="DD/MM/YYYY"
                          allowClear
                          placeholder={t('Seleziona data')}
                          defaultValue={dayjs(initialValues.actionDate)}
                          value={values.actionDate ? dayjs(values.actionDate) : null}
                          onChange={onChangeDate('actionDate')}
                          suffixIcon={<i className="far fa-calendar text-blue-links" />}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col lg={3} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Modalità contatto *')}</label>
                      <Form.Item
                        name="modalitaContatto"
                        className="mb-3"
                        initialValue={values.modalitaContatto}
                        rules={REQUIRED}
                      >
                        <Select
                          className="w-100"
                          placeholder={t('Seleziona modalità')}
                          onChange={handleChangeSelect('modalitaContatto')}
                          value={values.modalitaContatto}
                          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                        >
                          {Object.values(AgendaCommercialeDtoModalitaContattoEnum).map((val) => (
                            <Select.Option key={val} value={val}>
                              {val}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                {/* Row 2: Nota */}
                <Row>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Nota *')}</label>
                      <Form.Item
                        name="nota"
                        className="mb-3"
                        initialValue={values.nota}
                        rules={[
                          ...REQUIRED,
                          { max: 255, message: t('La nota non può superare i 255 caratteri') },
                        ]}
                      >
                        <Input.TextArea
                          name="nota"
                          placeholder={t('Inserisci nota')}
                          value={values.nota}
                          maxLength={255}
                          showCount
                          onChange={handleChange}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                {/* Opportunity: sempre visibile subito dopo la nota */}
                {prefilledOpportunity ? (
                  <Row>
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Opportunity associata')}</label>
                        <Input
                          className="w-100 mb-3"
                          disabled
                          style={{ backgroundColor: '#f5f5f5', color: '#555' }}
                          value={[
                            prefilledOpportunity.denominazioneOpportunity || prefilledOpportunity.pkOpportunity,
                            prefilledOpportunity.oppPercProb != null ? `${prefilledOpportunity.oppPercProb}%` : null,
                            prefilledOpportunity.oppImporto != null
                              ? prefilledOpportunity.oppImporto.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
                              : null,
                            prefilledOpportunity.dataPotenzialeAvvio
                              ? dayjs(prefilledOpportunity.dataPotenzialeAvvio).format('DD/MM/YYYY')
                              : null,
                          ].filter(Boolean).join(' · ')}
                        />
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <Row>
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{isWon ? t('Opportunity associata *') : t('Opportunity associata')}</label>
                        <Form.Item
                          name="fkOpportunity"
                          className="mb-3"
                          initialValue={values.fkOpportunity}
                        >
                          <Spin spinning={loadingOpportunities} size="small">
                            <Select
                              showSearch
                              className="w-100"
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                              }
                              allowClear
                              placeholder={loadingOpportunities ? t('Caricamento...') : t('Seleziona opportunity')}
                              onChange={handleChangeSelect('fkOpportunity')}
                              value={values.fkOpportunity}
                              disabled={loadingOpportunities || opportunities.length === 0 || !values.fkCliente}
                              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                            >
                              {opportunities.map((opp) => (
                                <Select.Option
                                  key={opp.pkOpportunity}
                                  value={opp.pkOpportunity}
                                  label={opp.denominazioneOpportunity || opp.pkOpportunity}
                                >
                                  <span>{opp.denominazioneOpportunity || opp.pkOpportunity}</span>
                                  <span style={{ color: '#888', marginLeft: 8, fontSize: '0.82rem' }}>
                                    {opp.statoOpportunity && `${opp.statoOpportunity.replace(/_/g, ' ')}`}
                                    {opp.oppPercProb != null && ` · ${opp.oppPercProb}%`}
                                    {opp.oppImporto != null && ` · ${opp.oppImporto.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}`}
                                    {opp.dataPotenzialeAvvio && ` · ${dayjs(opp.dataPotenzialeAvvio).format('DD/MM/YYYY')}`}
                                  </span>
                                </Select.Option>
                              ))}
                            </Select>
                          </Spin>
                        </Form.Item>
                        {isWon && !loadingOpportunities && opportunities.length === 0 && values.fkCliente && (
                          <Alert
                            type="error"
                            showIcon
                            className="mt-1"
                            message={t('Crea prima un\'opportunity in pipeline')}
                          />
                        )}
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Info esito */}
                <Alert
                  type="info"
                  showIcon
                  className="mb-3"
                  message={t('Una volta eseguita l\'attività, compila il campo "Esito" per registrarne il risultato.')}
                />

                {/* Row 3: Struttura di business (se visibile), Esito, Importo, Margine */}
                <Row>
                  {(showBusinessUnitSelect || prefilledOpportunity) && (
                    <Col lg={3} md={6} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Struttura di business *')}</label>
                        {prefilledOpportunity ? (
                          <Input
                            className="w-100 mb-3"
                            value={prefilledStrutturaName ?? String(prefilledOpportunity.fkStrutturaBusiness ?? '-')}
                            disabled
                            style={{ backgroundColor: '#f5f5f5', color: '#555' }}
                          />
                        ) : (
                          <Form.Item
                            name="fkStrutturaBusiness"
                            className="mb-3"
                            initialValue={values.fkStrutturaBusiness}
                            rules={REQUIRED}
                          >
                            <Select
                              showSearch
                              className="w-100"
                              optionFilterProp="children"
                              filterOption={filterOption}
                              allowClear={!isBusinessUnitReadonly}
                              disabled={isBusinessUnitReadonly}
                              placeholder={t('Seleziona struttura')}
                              onChange={(value) => setFieldValue('fkStrutturaBusiness', value)}
                              value={values.fkStrutturaBusiness ?? autoBusinessUnitId}
                              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                            >
                              {availableBusinessUnits.map((bu) => (
                                <Select.Option key={bu.id} value={bu.id}>
                                  {bu.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                      </div>
                    </Col>
                  )}
                  <Col lg={showBusinessUnitSelect ? 3 : 4} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Esito')}</label>
                      <Form.Item name="esito" className="mb-3" initialValue={values.esito}>
                        <Select
                          className="w-100"
                          placeholder={t('Seleziona esito')}
                          onChange={handleEsitoChange}
                          value={values.esito}
                          allowClear
                          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                          labelRender={({ value }) => {
                            const ESITO_COLORS: Record<string, string> = {
                              POSITIVO: '#4caf50',
                              NEGATIVO: '#ef4444',
                              NEUTRO: '#f59e0b',
                              ANNULLATA: '#6c757d',
                            };
                            const v = String(value ?? '');
                            const color = ESITO_COLORS[v];
                            if (!color) return <span>{esitoLabel(v)}</span>;
                            return (
                              <span
                                style={{
                                  display: 'inline-block',
                                  backgroundColor: color,
                                  color: '#fff',
                                  borderRadius: '12px',
                                  padding: '1px 10px',
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                }}
                              >
                                {esitoLabel(v)}
                              </span>
                            );
                          }}
                        >
                          {Object.values(AgendaCommercialeDtoEsitoEnum).map((val) => {
                            const ESITO_COLORS: Record<string, string> = {
                              POSITIVO: '#4caf50',
                              NEGATIVO: '#ef4444',
                              NEUTRO: '#f59e0b',
                              ANNULLATA: '#6c757d',
                            };
                            const color = ESITO_COLORS[val] ?? '#858796';
                            return (
                              <Select.Option key={val} value={val} label={esitoLabel(val)}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    backgroundColor: color,
                                    color: '#fff',
                                    borderRadius: '12px',
                                    padding: '1px 10px',
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {esitoLabel(val)}
                                </span>
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                  <Col lg={showBusinessUnitSelect ? 3 : 4} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Importo (€)')}</label>
                      <Form.Item name="amount" className="mb-3" initialValue={values.amount}>
                        <Input
                          type="number"
                          name="amount"
                          placeholder={t('Inserisci importo')}
                          value={values.amount}
                          onChange={handleChange}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col lg={showBusinessUnitSelect ? 3 : 4} md={6} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Margine (€)')}</label>
                      <Form.Item name="margin" className="mb-3" initialValue={values.margin}>
                        <Input
                          type="number"
                          name="margin"
                          placeholder={t('Inserisci margine')}
                          value={values.margin}
                          onChange={handleChange}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                {/* Nota esito (solo se esito selezionato) */}
                {values.esito && (
                  <Row>
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Nota esito *')}</label>
                        <Form.Item
                          name="notaEsito"
                          className="mb-3"
                          initialValue={values.notaEsito}
                          rules={[
                            ...REQUIRED,
                            { max: 255, message: t('La nota esito non può superare i 255 caratteri') },
                          ]}
                        >
                          <Input.TextArea
                            name="notaEsito"
                            placeholder={t('Inserisci nota esito')}
                            value={values.notaEsito}
                            maxLength={255}
                            showCount
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Opportunity spostato dopo Nota */}

                {/* Richiesta supporto a (opzionale) — in coda al form */}
                <Row>
                  <Col lg={6} md={12} sm={12} xs={12}>
                    <div className="form-group">
                      <label>{t('Richiesta supporto a')}</label>
                      <Form.Item name="fkUtente" className="mb-3" initialValue={values.fkUtente}>
                        <Select
                          showSearch
                          allowClear
                          className="w-100"
                          optionFilterProp="children"
                          filterOption={filterOption}
                          placeholder={t('Seleziona utente')}
                          onChange={(value) => setFieldValue('fkUtente', value)}
                          value={values.fkUtente}
                          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                        >
                          {allUsers?.map((u) => (
                            <Select.Option key={u.id} value={u.id}>
                              {u.nominativo}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <LinksButton type="button" className="btn btn-links-outline" onClick={() => setShow(false)}>
                    {t('common.cancel')}
                  </LinksButton>
                  <LinksButton
                    type="submit"
                    className="btn-primary btn-links"
                    disabled={
                      (isWon && !loadingOpportunities && (opportunities.length === 0 || !values.fkOpportunity)) ||
                      (!!values.esito && !values.notaEsito?.trim())
                    }
                  >
                    {isEdit ? t('common.save') : t('common.add')}
                  </LinksButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddAgendaActionModal;
